<template>
    <b-container fluid>
        <b-row>
            <b-col>
                <b-table id="prescription-table" striped select-mode="single" responsive="true" :items="prescriptions"
                    :fields="fields">
                    <template #cell(start)="data">
                        <b-button @click="createPa(data.item)" variant="success" size="sm"
                            :disabled="!canCreatePA || isLoading || isProcessing(data.item)"
                            v-b-tooltip.hover="!canCreatePA ? 'You do not have permission to create Prior Authorizations' : ''">
                            Start
                        </b-button>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <CoverMyMedsResponseComponent v-model="cmmResponse" :displayValue="displayValue" v-show="!isLoading" />
        <div v-if="isLoading">
            <b-spinner label="Spinning" class="mr-2" />
            <strong>Starting PA...</strong>
        </div>
    </b-container>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import { Prescription } from '@/models/Prescription';
import { CoverMyMedsResponse, PriorAuthorization } from '@/models/PriorAuthorization';
import CoverMyMedsResponseComponent from "@/components/PriorAuthorization/CoverMyMedsResponseComponent.vue";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Permission } from '@/models/Permission';

@Component({
    name: "PriorAuthorizationCreate",
    components: {
        CoverMyMedsResponseComponent,
    },
})
export default class PriorAuthorizationCreate extends Vue {
    @Prop() private patientId!: number;
    @Prop() private value!: Prescription;
    private cmmResponse: CoverMyMedsResponse = {} as CoverMyMedsResponse;
    private prescriptions: Prescription[] = [];
    private isLoading = false;
    private processingPrescriptionIds: Set<number> = new Set();

    private fields: any[] = [
        { key: "storeID" },
        { key: "rxNumber" },
        { key: "rfNumber" },
        { key: "quantity" },
        { key: "refills" },
        { key: "productNameShort" },
        { key: "writtenDate", formatter: this.formatDate },
        { key: "start", label: "" },
    ];

    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
        this.fetchPrescriptionsForPriorAuthorization();
    }

    beforeDestroy() {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
    }

    formatDate(d: Date): string {
        if (!d) return "";
        return moment(d).format("L");
    }

    get displayValue(): string {
        return `${this.value.rxID} ${this.value.productNameShort} - ${this.formatDate(this.value.writtenDate)}`
    }

    get canCreatePA(): boolean {
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName.toLowerCase() === "services/prior authorization" ||
            p.permissionCodeName.toLowerCase() === "patient dashboard/create pa"
        );
    }

    isProcessing(item: Prescription): boolean {
        return this.processingPrescriptionIds.has(item.rxID);
    }

    async createPa(item: Prescription) {
        // Prevent duplicate PA creation for the same prescription
        if (this.processingPrescriptionIds.has(item.rxID)) {
            console.warn('PA creation already in progress for prescription:', item.rxID);
            return;
        }

        // Verify prescription still exists in the list
        if (!this.prescriptions.some(p => p.rxID === item.rxID)) {
            console.warn('Prescription no longer in list:', item.rxID);
            return;
        }

        this.$emit('input', Object.assign(new Prescription(), item));
        console.log("data: ", JSON.stringify(item));
        this.isLoading = true;
        this.processingPrescriptionIds.add(item.rxID);

        try {
            const response = await Axios.post<PriorAuthorization>(`/PriorAuthorization/Create?forcePA=true`, item);
            console.log("createPa response.data: ", response.data);

            // Check if PA was actually created (has an ID)
            if (!response.data.id) {
                this.$notification(NotificationOptions.error(
                    "Unable to create Prior Authorization. Missing required prescription or billing data."
                ));
                // Remove from list since it can't be processed
                const index = this.prescriptions.findIndex(p => p.rxID === item.rxID);
                if (index > -1) {
                    this.prescriptions.splice(index, 1);
                }
                return;
            }

            // Set CoverMyMeds response if available
            if (response.data.responses?.length) {
                const resp = response.data.responses[0];
                this.cmmResponse = resp;
                this.$notification(NotificationOptions.success("Prior Authorization initiated successfully"));
            } else {
                this.$notification(NotificationOptions.warning("Prior Authorization created, but no CoverMyMeds response was received."));
            }

            const index = this.prescriptions.findIndex(p => p.rxID === item.rxID);
            if (index > -1) {
                this.prescriptions.splice(index, 1);
            }

            if (this.prescriptions.length === 0) {
                this.$bvModal.hide("prior-authorization-create-modal");
            }
        } catch (err) {
            console.error('Error while creating PA', err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoading = false;
            this.processingPrescriptionIds.delete(item.rxID);
        }
    }

    @Watch('patientId')
    async fetchPrescriptionsForPriorAuthorization() {
        console.log("fetch that rx");
        try {
            const response = await Axios.get(`/PriorAuthorization/PossiblePrescriptions/${this.patientId}`);
            console.log("rx's response.data: ", response.data);
            this.prescriptions = response.data;
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        }
    }

}
</script>